import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { announcementsApi } from './api'
import type { AnnouncementDraftPayload } from './types'

const QK = {
  adminList: ['platform', 'announcements'] as const,
  adminOne: (id: number) => ['platform', 'announcements', id] as const,
  userList: ['announcements', 'user'] as const,
  userUnread: ['announcements', 'unread-count'] as const,
}

export function useAdminAnnouncements() {
  return useQuery({ queryKey: QK.adminList, queryFn: announcementsApi.adminList })
}

export function useAdminAnnouncement(id: number | null) {
  return useQuery({
    queryKey: QK.adminOne(id ?? 0),
    queryFn: () => announcementsApi.adminGet(id as number),
    enabled: !!id,
  })
}

export function useCreateDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: AnnouncementDraftPayload) => announcementsApi.adminCreateDraft(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminList }),
  })
}

export function useUpdateDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; payload: Partial<AnnouncementDraftPayload> }) =>
      announcementsApi.adminUpdateDraft(vars.id, vars.payload),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: QK.adminList })
      qc.invalidateQueries({ queryKey: QK.adminOne(vars.id) })
    },
  })
}

export function useDeleteDraft() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => announcementsApi.adminDeleteDraft(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK.adminList }),
  })
}

export function usePublishAnnouncement() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => announcementsApi.adminPublish(id),
    onSuccess: (_d, id) => {
      qc.invalidateQueries({ queryKey: QK.adminList })
      qc.invalidateQueries({ queryKey: QK.adminOne(id) })
    },
  })
}

// Customer-facing
export function useUserAnnouncements() {
  return useQuery({
    queryKey: QK.userList,
    queryFn: announcementsApi.userList,
    staleTime: 0,
  })
}

export function useUserAnnouncementsUnreadCount() {
  return useQuery({
    queryKey: QK.userUnread,
    queryFn: announcementsApi.userUnreadCount,
    refetchInterval: 60_000,
  })
}

export function useMarkAnnouncementRead() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => announcementsApi.userMarkRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.userList })
      qc.invalidateQueries({ queryKey: QK.userUnread })
    },
  })
}
